/**
 * Extracts the domain name and the full path from a URL string.
 * Removes 'http://', 'https://', and 'www.' prefixes.
 * Removes trailing slash from the path.
 *
 * @param urlString The URL string to process.
 * @returns The extracted domain and full path (e.g., 'example.com/path/subpath'),
 *          or null if the URL is invalid or cannot be parsed.
 */
export function extractDomainWithPath(urlString: string): string | null {
  if (!urlString || typeof urlString !== 'string') {
    return null
  }

  let normalizedUrl = urlString.trim()

  if (!normalizedUrl || /\s/.test(normalizedUrl)) {
    return null
  }

  // Add protocol if missing for URL constructor
  if (!/^https?:\/\//i.test(normalizedUrl)) {
    normalizedUrl = `https://${normalizedUrl}`
  }

  try {
    const url = new URL(normalizedUrl)
    let hostname = url.hostname

    // Remove 'www.' prefix if present
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4)
    }

    // Get the pathname and remove trailing slash if it exists and isn't the only character
    let pathname = url.pathname
    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.substring(0, pathname.length - 1)
    }

    // Return hostname + pathname if pathname is not just '/'
    if (pathname && pathname !== '/') {
      return `${hostname}${pathname}` // Append pathname directly (it starts with '/')
    } else {
      return hostname // Return only hostname if no path or path is just '/'
    }
  } catch (_error) {
    // Handle invalid URLs - attempt to extract domain-like part and path
    // This is a fallback for cases like 'example.com/path/subpath' without protocol
    const trimmedUrl = urlString.trim()
    // Remove potential protocol for simpler matching
    const urlWithoutProtocol = trimmedUrl.replace(/^https?:\/\//i, '')
    const domainPathMatch = urlWithoutProtocol.match(/^([^/]+)(\/.*)?$/)

    if (domainPathMatch && domainPathMatch[1]) {
      let potentialDomain = domainPathMatch[1]
      if (potentialDomain.startsWith('www.')) {
        potentialDomain = potentialDomain.substring(4)
      }

      // Basic check if it looks like a domain
      if (potentialDomain.includes('.')) {
        let pathPart = domainPathMatch[2] || '' // Get the path part or empty string
        // Remove trailing slash from pathPart if it exists and isn't the only character
        if (pathPart.length > 1 && pathPart.endsWith('/')) {
          pathPart = pathPart.substring(0, pathPart.length - 1)
        }

        // Return domain + path if path exists, otherwise just domain
        return pathPart ? `${potentialDomain}${pathPart}` : potentialDomain
      }
    }

    // If still cannot parse, return null
    return null
  }
}
